import type { CheckoutQuote } from '@/lib/types/billing';
import { formatCents } from '@/lib/utils/format.utils';
import { BILLING_STRINGS } from '../constants/billing-strings';
import { formatAccessTerm } from '../access-term';
import { INTERVAL_SUFFIX } from './PlanSummaryCard';

const COPY = BILLING_STRINGS.REVIEW;

export interface OrderSummaryProps {
  quote: CheckoutQuote;
}

/** Cents as money, never `undefined` in the middle of a sentence. */
function money(cents: number): string {
  return formatCents(cents) ?? '';
}

/**
 * The priced order on the review step (Sprint 7 QA).
 *
 * Renders the backend's quote line by line and adds nothing up itself, so the
 * only line is the plan (no seat is sold since v1.2, FR-138) and the
 * "Due today" here is the amount Stripe will charge. Below it, what happens
 * next: the renewal amount once any limited discount ends, or the access term
 * of a one-time purchase, which never renews.
 */
export function OrderSummary({ quote }: OrderSummaryProps) {
  const suffix = INTERVAL_SUFFIX[quote.billingInterval];
  const discount = quote.discount;

  return (
    <div className="flex flex-col gap-4">
      <dl className="flex flex-col gap-2 text-sm">
        {quote.lines.map((line) => (
          <div key={line.kind} className="flex justify-between gap-4">
            <dt className="text-text-secondary">{quote.planName}</dt>
            <dd className="text-text-primary">{money(line.amountCents)}</dd>
          </div>
        ))}
        {discount && (
          <div className="flex justify-between gap-4">
            <dt className="text-text-secondary">{COPY.LINE_DISCOUNT.replace('{code}', discount.code)}</dt>
            <dd className="text-dkgreen">-{money(discount.amountCents)}</dd>
          </div>
        )}
      </dl>

      <div className="border-border-strong flex items-baseline justify-between gap-4 border-t pt-4">
        <span className="text-text-primary text-base font-semibold">{COPY.DUE_TODAY}</span>
        <span className="text-text-primary text-2xl font-semibold">{money(quote.totalDueCents)}</span>
      </div>

      <div className="text-text-muted flex flex-col gap-1 text-xs">
        {discount && quote.renewalCents !== null && (
          <p>
            {discount.durationMonths
              ? COPY.DISCOUNT_MONTHS.replace('{count}', String(discount.durationMonths))
              : COPY.DISCOUNT_ONCE}
          </p>
        )}
        {quote.renewalCents === null ? (
          <p>{COPY.ONE_TIME_NOTE.replace('{term}', formatAccessTerm(quote) ?? '')}</p>
        ) : (
          <p>
            {COPY.RENEWS_AT.replace('{amount}', money(quote.renewalCents)).replace('{interval}', suffix)}
          </p>
        )}
      </div>
    </div>
  );
}
